import { nextCycleReset } from '@xver/utils/date';
import DialogBox from '@xver/components/Atoms/DialogBox/DialogBox';
import { useTranslation } from 'react-i18next';

import { useContainerCostQuery } from '../hooks/useContainerCostQuery';

interface CostCardProps {
    containerId: string;
}

// The shared `format` util renders 0 as '-', which would wrongly hide the
// within-free-quota ฿0 state, so cost values use this zero-aware formatter.
function money(value: number | undefined, loading: boolean): string {
    if (loading) return '-';
    return (value ?? 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
    });
}

export default function CostCard({ containerId }: CostCardProps) {
    const { t } = useTranslation('containerEngineInstance');
    const { data, isPending } = useContainerCostQuery(containerId);

    const loading = isPending && !data;
    const cycleResetLabel = nextCycleReset(data?.period);

    const footer = (
        <div className="flex justify-between items-center text-sm">
            <span className="text-fg/68">{t('cost.cycleResets')}</span>
            <span className="text-fg">{loading ? '-' : cycleResetLabel}</span>
        </div>
    );

    return (
        <DialogBox
            label={t('cost.title')}
            description={t('cost.description')}
            classNames="p-4 gap-4"
            footer={footer}
        >
            <div className="w-full flex flex-row justify-between items-start gap-4">
                <div className="flex flex-col items-start">
                    <span className="text-sm text-fg/68">{t('cost.hourly')}</span>
                    <div className="text-2xl font-semibold text-fg">
                        ฿{money(data?.hourly_price, loading)}
                        <span className="text-base text-fg/68" style={{ fontWeight: 400 }}>
                            {' '}
                            {t('cost.perHourSuffix')}
                        </span>
                    </div>
                </div>
                <div className="flex flex-col items-end">
                    <span className="text-sm text-fg/68">{t('cost.currentCharges')}</span>
                    <div className="text-2xl font-semibold text-fg">
                        ฿{money(data?.total_charges, loading)}
                    </div>
                </div>
            </div>
        </DialogBox>
    );
}
